<script lang="ts">
  import { onMount } from "svelte"
  import { Expand } from "lucide-svelte"
  import type { AxiosResponse } from "axios"
  import { Pulse } from "svelte-loading-spinners"
  import { xhr, convertObjectToUrl, toast } from "$app/utils"
  import Recognition from "$app/Pages/Plans/Recognitions/Recognition.svelte"
  import PlanOverview from "$app/Pages/Plans/Recognitions/PlanOverview.svelte"
  import RecognitionSlide from "$app/Pages/Plans/Recognitions/RecognitionSlide.svelte"
  import ValueSlide from "$app/Pages/Plans/Recognitions/ValueSlide.svelte"
  import Filters from "./Filters/Index.svelte"
  import TopRecognized from "$app/Pages/Plans/Recognitions/TopRecognized.svelte"
  import TopGivers from "$app/Pages/Plans/Recognitions/TopGivers.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Splide from "@splidejs/splide"
  import screenfull from "screenfull"
  import { appStore } from "$app/store.svelte"
  import "@splidejs/splide/css"

  interface Props {
    plan_id: number
    user_stats: any
    mode?: string
  }

  let { plan_id, user_stats, mode = "list" }: Props = $props()

  let top_recognized: any = $state([])
  let top_givers: any = $state([])
  type keyStringObjectType = {
    [key: string]: string
  }

  const dates: any = appStore.dates
  const quarter = `q${dates.quarter}-${dates.year}`

  const current_user = appStore.current_user
  const users = appStore.users
  const plan_review = appStore.plan_review

  let isLoading = $state(false)
  let isLoadingStats = $state(false)
  let isLoadingMore = $state(false)

  let currentFilter = { date_filter: quarter }

  const regExpQuarter = new RegExp(/^q[1-4]\-[2-9]\d{3}$/)

  const dateFilter = appStore.params?.date_filter
  if (dateFilter && regExpQuarter.test(dateFilter)) {
    currentFilter = { date_filter: dateFilter }
  } else {
    appStore.params.date_filter = quarter
  }

  let divElem: HTMLDivElement = $state()
  let vis = true
  function checkInfiniteLoad(): void {
    const totalPageHeight = document.body.scrollHeight
    const scrollPoint = window.scrollY + window.innerHeight + 500

    const visible = scrollPoint >= totalPageHeight

    if (visible && vis) {
      vis = false
      getRecognitions(currentFilter)
      setTimeout(() => (vis = true), 1000)
    }
  }

  onMount(async () => {
    isLoading = true
    appStore.recognitions = []
    getRecognitions(currentFilter)
    getStats(currentFilter)

    let prev = new Date().getTime()
    let now = 0
    const delay = 10
    window.onscroll = function () {
      now = new Date().getTime()
      if (now - prev > delay) {
        checkInfiniteLoad()
        prev = now
      }
    }
  })

  let page = 1
  let totalPages = 1
  // Get all statements for this section
  async function getRecognitions(
    filter: keyStringObjectType,
    reset = false
  ): Promise<void> {
    if (reset) {
      totalPages = 0
      page = 1
      appStore.recognitions = []
      isLoading = true
    }

    if (totalPages && page > totalPages) {
      isLoading = false
      return
    }

    if (page > 1) {
      isLoadingMore = true
    }
    try {
      const queryStr = convertObjectToUrl(filter)
      const resp: AxiosResponse<RecognitionType[]> = await xhr.get(
        `/plans/${plan_id}/recognitions/filter.json?${queryStr}&page=${page}`
      )
      totalPages = +resp.headers["total-pages"]
      page++
      appStore.recognitions = [...appStore.recognitions, ...resp.data]

      window.history.pushState(filter, "Recognitions", `?${queryStr}`)
    } catch (e) {
      isLoading = false
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the recognitions", "error")
    } finally {
      isLoading = false
      isLoadingMore = false
    }
  }

  let currentFilterHeading = $state("")

  async function getStats(filter: keyStringObjectType): Promise<void> {
    isLoadingStats = true
    try {
      const queryStr = convertObjectToUrl(filter)
      const resp = await xhr.get(
        `/plans/${plan_id}/recognitions/stats.json?${queryStr}`
      )
      currentFilterHeading = resp.data.filter_heading
      top_givers = resp.data.top_givers
      top_recognized = resp.data.top_recognized
    } catch (e: any) {
      isLoadingStats = false
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the recognition stats", "error")
    } finally {
      isLoadingStats = false
    }
  }

  let scrollY: number = $state()

  const deleteRecognition = async function (recognition: RecognitionType) {
    const idx = appStore.recognitions.findIndex((r) => r.id === recognition.id)
    appStore.recognitions[idx]._selected = true

    setTimeout(async () => {
      if (confirm("Are you sure you want to delete the recognition?")) {
        try {
          await xhr.delete(
            `/plans/${recognition.plan_id}/recognitions/${recognition.id}`
          )

          appStore.recognitions = appStore.recognitions.filter(
            (r) => r.id !== recognition.id
          )
        } catch (e: any) {
          if (typeof Honeybadger.notify !== "undefined") {
            Honeybadger.notify(e)
          }
          toast("There was an error deleting the recognition", "error")
        }
      } else {
        appStore.recognitions[idx]._selected = false
        appStore.recognitions = appStore.recognitions
      }
    })
  }

  function updateFilters(detail) {
    currentFilter = detail
    getRecognitions(currentFilter, true)
    getStats(currentFilter)
  }

  let fullscreenSlider: any = $state()
  function toggleFullscreenSlider() {
    if (screenfull.isEnabled) {
      if (!screenfull.element) {
        fullscreenSlider.classList.add("p-20")
      } else {
        fullscreenSlider.classList.remove("p-20")
      }
      screenfull.toggle(fullscreenSlider)
    }
  }

  var splide = null
  function loadSlideshow() {
    if (mode === "slideshow") {
      splide = new Splide(".splide", {
        type: "slide",
        rewind: true,
        speed: 800,
        autoplay: true,
        pagination: false,
        pauseOnFocus: false,
        pauseOnHover: false,
        interval: 20000
      }).mount()
    }
  }
</script>

<div
  class="grid grid-cols-4 gap-4 items-start bg-lwp-pale-grey"
  bind:this={fullscreenSlider}
>
  <div class="col-span-4 {mode === 'list' ? 'lg:col-span-3' : ''}">
    <section>
      <div class="mb-4">
        <Filters onsearch={(detail) => updateFilters(detail)} />
      </div>

      {#if isLoading}
        <div class="py-6 w-full">
          <div class="text-center flex justify-center">
            <Pulse size="140" color="#cdcdcd" unit="px" duration="1s" />
          </div>
        </div>
      {:else if mode === "list"}
        <div>
          <ul
            class="gap-y-4 w-full rounded-sm"
            data-name="recognitions-list"
            id="infinite-scroll"
          >
            {#each appStore.recognitions as recognition (recognition.id)}
              {#key recognition._selected}
                <Recognition
                  {recognition}
                  ondelete={() => deleteRecognition(recognition)}
                />
              {/key}
            {/each}

            {#if isLoading === false && appStore.recognitions?.length === 0}
              <li
                class="bg-white sm:flex sm:space-x-5 p-6 border-gray-100 rounded-sm"
              >
                <p class="font-lg font-bold text-gray-900 mb-3">
                  No recognitions found that match your filters.
                </p>
              </li>
            {/if}

            {#if isLoadingMore}
              <div class="py-6 w-full">
                <div class="text-center flex justify-center">
                  <Pulse size="140" color="#cdcdcd" unit="px" duration="1s" />
                </div>
              </div>
            {/if}
          </ul>

          <div bind:this={divElem} class="h-6 w-full">&nbsp;</div>
        </div>
      {:else if mode === "slideshow"}
        <div class="relative">
          {#if appStore.recognitions.length > 0}
            <div class="mt-8 splide" use:loadSlideshow>
              <div class="splide__track mx-0">
                <button
                  class="absolute top-1 right-1 z-40"
                  onclick={toggleFullscreenSlider}
                >
                  <Expand class="size-5 text-gray-600 hover:text-blue-800" />
                </button>
                <ul class="splide__list">
                  {#each appStore.recognitions as recognition, counter (recognition.id)}
                    <RecognitionSlide {recognition} />
                    <ValueSlide {counter} />
                  {/each}
                </ul>
              </div>
            </div>
          {:else}
            <div class="mt-8">
              <div class="text-center py-16 lg:py-32 text-2xl lg:text-4xl">
                <span class="p-8 bg-amber-50 rounded shadow text-amber-900"
                  >No recognitions found that match your filters.</span
                >
              </div>
            </div>
          {/if}

          <PlanOverview />
        </div>
      {/if}
    </section>
  </div>

  {#if mode === "list"}
    <div class="col-span-4 lg:col-span-1">
      <!-- Right column -->
      <div class="absolute hidden lg:block sm:relative gap-4">
        <!-- Profile -->
        <section aria-labelledby="announcements-title">
          <div class="bg-white overflow-hidden shadow-lg">
            <div class="p-6">
              <div class="flow-root mt-6">
                <div class="flex flex-col border-b border-gray-200 pb-4 mb-4">
                  <img
                    src={appStore.current_user.avatar_thumb_url}
                    class="mx-auto h-32 w-32 rounded-full shadow"
                    alt={appStore.current_user.name}
                  />
                  <h2
                    class="text-lg font-medium text-gray-900 text-center mt-2"
                  >
                    {appStore.current_user.name}
                  </h2>
                  {#if appStore.current_user.title}
                    <p class="text-base text-gray-700 text-center">
                      {appStore.current_user.title}
                    </p>
                  {/if}
                </div>
                <div class="flex justify-around">
                  <div class="flex flex-col justify-center">
                    <p class="text-2x font-bold text-center">
                      {user_stats.given}
                    </p>
                    <p class="text-gray-700 text-center">Given</p>
                    <button
                      class="text-xs text-blue-600 hover:underline"
                      onclick={() =>
                        getRecognitions(
                          { date_filter: "all", sender_ids: [current_user.id] },
                          true
                        )}>View All Given</button
                    >
                  </div>
                  <div class="flex flex-col justify-center">
                    <p class="text-2x font-bold text-center">
                      {user_stats.received}
                    </p>
                    <p class="text-gray-700 text-center">Received</p>
                    <button
                      class="text-xs text-blue-600 hover:underline"
                      onclick={() =>
                        getRecognitions(
                          {
                            date_filter: "all",
                            recipient_ids: [current_user.id]
                          },
                          true
                        )}>View All Received</button
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <TopRecognized
          {currentFilterHeading}
          {isLoadingStats}
          {top_recognized}
        />

        <TopGivers {currentFilterHeading} {isLoadingStats} {top_givers} />
      </div>
    </div>
  {/if}
</div>

<svelte:window bind:scrollY />

<style>
  .fullscreen {
    z-index: 9999;
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
  }
</style>
